import type { BetterFeaturePluginSchema } from "@di-devs/better-feature/types";
import { z } from "zod";
import { AUDIENCE_STATUS_VALUES } from "../constants/audience.constants";

// Base response schema
export const responseSchema = z.object({
	message: z.coerce.string(),
	success: z.coerce
		.string()
		.transform((val) => val === "true" || val === "1")
		.refine((val) => typeof val === "boolean", {
			message:
				"Query param must be 'true' or '1', otherwise it will default to false",
		}),
});

// Audience search parameters schema
export const audienceSearchSchema = z
	.object({
		// Basic filters
		address: z.coerce.string().nullable().optional(),
		memberSinceMin: z.coerce.string().nullable().optional(),
		memberSinceMax: z.coerce.string().nullable().optional(),
		isCampaign: z.coerce.boolean().nullable().optional(),
		hasWon: z.coerce.boolean().nullable().optional(),
		candidateAtMin: z.coerce.string().nullable().optional(),
		candidateAtMax: z.coerce.string().nullable().optional(),

		// Date range filters
		createdAtMin: z.coerce.string().nullable().optional(),
		createdAtMax: z.coerce.string().nullable().optional(),
		lastVisitMin: z.coerce.string().nullable().optional(),
		lastVisitMax: z.coerce.string().nullable().optional(),

		// Status filters
		messages: z.enum(["read", "unread"]).nullable().optional(),
		isFriends: z.coerce.boolean().nullable().optional(),
		isRegistered: z.coerce.boolean().nullable().optional(),
		membershipStatus: z
			.enum(["provisional", "active", "expired", "deactivated"])
			.nullable()
			.optional(),

		// Payment filters
		paymentMethod: z.enum(["card", "on_site"]).nullable().optional(),
		paymentCompletedAtMin: z.coerce.string().nullable().optional(),
		paymentCompletedAtMax: z.coerce.string().nullable().optional(),

		// Event filters
		eventCategoryId: z.coerce.number().int().nullable().optional(),
		eventOccasionId: z.coerce.number().int().nullable().optional(),
		participationStatus: z
			.enum(["participate", "reserve", "cancel"])
			.nullable()
			.optional(),
		occurrenceStartAt: z.coerce.string().nullable().optional(),
		occurrenceEndAt: z.coerce.string().nullable().optional(),

		// Audience type
		type: z
			.enum(["member", "event", "default", "campaign", "survey", "lottery"])
			.optional(),
	})
	.passthrough(); // Allow additional memberAttributeFilter* properties

// Audience creation schema
export const audienceCreateSchema = audienceSearchSchema.extend({
	audienceName: z.coerce.string().min(1, "Audience name is required"),
});

// Core audience data schema
export const audienceSchema = z.object({
	audienceGroupId: z.coerce.string(),
	description: z.coerce.string().optional(),
	audienceCount: z.coerce.number().int().min(0),
	searchCondition: z.any().nullable().optional(),
	remarks: z.any().nullable().optional(),
	expireTimestamp: z.coerce.number().int().optional(),
	createdAt: z.coerce.date().optional(),
	updatedAt: z.coerce.date().optional(),
});

// Extended audience with LINE API data
export const audienceWithStatusSchema = audienceSchema.extend({
	status: z.enum(AUDIENCE_STATUS_VALUES),
	created: z.coerce.number(),
	expireTimestamp: z.coerce.number().optional(),
});

// Better Feature plugin schema
export const audienceBetterFeatureSchema = {
	audience: {
		fields: {
			audienceGroupId: { type: "string", required: true, unique: true },
			description: { type: "string", required: false },
			audienceCount: { type: "number", required: true },
			searchCondition: { type: "string", required: false },
			remarks: { type: "string", required: false },
			type: { type: "string", required: false },
		},
	},
} satisfies BetterFeaturePluginSchema;
